import { api } from '../lib/api';

export interface Event {
  id: string;
  title: string;
  description: string;
  isClanMeeting: boolean;
  location: string | null;
  startTime: string;
  signupDeadline: string | null;
  endTime: string | null;
  imageUrl: string | null;
  organizerId: string | null;
  createdAt: string;
  updatedAt: string;
  organizer: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  } | null;
  organizers: Array<{
    id: string;
    userId: string;
    user: {
      id: string;
      name: string;
      profileImageUrl: string | null;
    };
  }>;
  attendees: Array<{
    id: string;
    status: 'attending' | 'maybe' | 'not_attending';
    user: {
      id: string;
      name: string;
      profileImageUrl: string | null;
    };
  }>;
  noResponse?: Array<{
    id: string;
    name: string;
    profileImageUrl: string | null;
  }>;
  _count: {
    attendees: number;
  };
}

export interface EventDetail extends Event {
  attendees: EventAttendee[];
}

export interface EventAttendee {
  id: string;
  eventId: string;
  userId: string;
  status: 'attending' | 'maybe' | 'not_attending';
  createdAt: string;
  user: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
}

export interface CreateEventInput {
  title: string;
  description: string;
  isClanMeeting: boolean;
  location?: string;
  startTime: string;
  signupDeadline?: string;
  endTime?: string;
  imageUrl?: string;
  organizerIds: string[];
}

export interface UpdateEventInput {
  title?: string;
  description?: string;
  isClanMeeting?: boolean;
  location?: string;
  startTime?: string;
  signupDeadline?: string;
  endTime?: string;
  imageUrl?: string;
  organizerIds?: string[];
}

export type RsvpStatus = 'attending' | 'maybe' | 'not_attending';

/**
 * Get all upcoming events
 */
export const getUpcomingEvents = async (): Promise<Event[]> => {
  const response = await api.get<Event[]>('/events');
  return response.data;
};

/**
 * Get archived (past) events
 */
export const getArchivedEvents = async (): Promise<Event[]> => {
  const response = await api.get<Event[]>('/events/archived');
  return response.data;
};

/**
 * Get event by ID
 */
export const getEventById = async (eventId: string): Promise<EventDetail> => {
  const response = await api.get<EventDetail>(`/events/${eventId}`);
  return response.data;
};

/**
 * Create a new event
 */
export const createEvent = async (input: CreateEventInput): Promise<Event> => {
  const response = await api.post<Event>('/events', input);
  return response.data;
};

/**
 * Update an event
 */
export const updateEvent = async (
  eventId: string,
  input: UpdateEventInput
): Promise<Event> => {
  const response = await api.patch<Event>(`/events/${eventId}`, input);
  return response.data;
};

/**
 * Delete an event
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  await api.delete(`/events/${eventId}`);
};

/**
 * RSVP to an event
 */
export const rsvpToEvent = async (
  eventId: string,
  status: RsvpStatus
): Promise<EventAttendee> => {
  const response = await api.post<EventAttendee>(
    `/events/${eventId}/rsvp`,
    { status }
  );
  return response.data;
};

/**
 * Get user's RSVP status for an event
 */
export const getUserRsvp = async (
  eventId: string
): Promise<EventAttendee | null> => {
  const response = await api.get<EventAttendee | null>(
    `/events/${eventId}/rsvp/me`
  );
  return response.data;
};

/**
 * Get all attendees for an event
 */
export const getEventAttendees = async (
  eventId: string
): Promise<EventAttendee[]> => {
  const response = await api.get<EventAttendee[]>(
    `/events/${eventId}/attendees`
  );
  return response.data;
};

/**
 * Get events user hasn't responded to (pending RSVP)
 */
export const getPendingRSVPEvents = async (): Promise<{ events: Event[]; count: number }> => {
  const response = await api.get<{ events: Event[]; count: number }>('/events/pending/rsvp');
  return response.data;
};

/**
 * Send event_invite notification to all users (organizer/admin only)
 */
export const sendEventNotification = async (eventId: string): Promise<{ notified: number }> => {
  const response = await api.post<{ notified: number }>(`/events/${eventId}/notify`);
  return response.data;
};

/**
 * Admin: set RSVP for a specific user
 */
export const adminSetRsvp = async (
  eventId: string,
  targetUserId: string,
  status: RsvpStatus
): Promise<EventAttendee> => {
  const response = await api.post<EventAttendee>(
    `/events/${eventId}/rsvp/${targetUserId}`,
    { status }
  );
  return response.data;
};

/**
 * Admin: remove RSVP for a specific user
 */
export const adminRemoveRsvp = async (
  eventId: string,
  targetUserId: string
): Promise<void> => {
  await api.delete(`/events/${eventId}/rsvp/${targetUserId}`);
};
